import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import {
  faArrowRight,
  faCalendar,
  faChalkboardUser,
  faClock,
  faIndent,
  faLocationDot,
  faStairs,
  faUser,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { FormatSettings } from '@progress/kendo-angular-dateinputs';
import { DialogService } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import * as moment from 'moment';
import { Observable, combineLatest, of, takeUntil } from 'rxjs';
import { EvaluationsTabComponent } from 'src/app/modules/dashboard/components/evaluations-tab/evaluations-tab.component';
import {
  DashboardEvalResponse,
  DashboardResponse,
  EvaluationList,
} from 'src/app/modules/dashboard/interface/dashboard-response';
import { DashboardService } from 'src/app/modules/dashboard/services/dashboard.service';
import { iepSections } from 'src/app/modules/iep/iep-document/constants/iep-sections';
import { IEPService } from 'src/app/modules/iep/iep-document/services/iep.services';
import { SCHEDULE_MEETING_CONSTANTS } from 'src/app/modules/student-info/schedule-meeting/schedule-meeting-constants';
import { AuthStore } from 'src/app/stores/auth.store';
import { ReferenceStore } from 'src/app/stores/reference.store';
import {
  PENDING_SCHEDULING,
  SCHEDULED,
  TAG_MAPPER_LENGTH,
  ZERO_MONTH_DIFFERENCE,
} from '../../constants/assessment-scheduling';
import {
  ADDITIONAL_MINUTE,
  HOURS,
  MINUTES,
  MS,
  SECONDS,
} from '../../constants/time-constants';
import { MeetingSettingType } from '../../enums/meeting-setting-type';
import { MeetingType } from '../../enums/meeting-type';
import { IReferenceData } from '../../interfaces/form/reference-data.model';
import { ISedmsFormModel } from '../../interfaces/form/sedms-form-model';
import { AssessmentScheduleMeetingFormModel } from '../../models/assessment-schedule-meeting/assessment-schedule-meeting-formmodel';
import { AssessmentScheduleMeetingRefData } from '../../models/assessment-schedule-meeting/assessment-schedule-meeting-reference.model';
import { AssessmentScheduleMeetingRequestModel } from '../../models/assessment-schedule-meeting/assessment-schedule-meeting-request-model';
import { AssessmentScheduleMeetingSubmitModel } from '../../models/assessment-schedule-meeting/assessment-schedule-meeting-submit-model';
import { ScheduleMeetingRequestModel } from '../../models/schedule-meeting-models/schedule-meeting-request-model';
import {
  Assessment,
  SubmitScheduleMeetingModel,
} from '../../models/schedule-meeting-models/submit-schedule-meeting-model';
import { User } from '../../models/user';

import { EvaluationService } from 'src/app/modules/evaluations/services/evaluation.service';
import { IMeetingDetails } from '../../interfaces/meeting-details';
import { AbstractForm } from '../abstract-form';

@Component({
  selector: 'app-assessment-schedule-meeting',
  templateUrl: './assessment-schedule-meeting.component.html',
  styleUrls: ['./assessment-schedule-meeting.component.scss'],
  standalone: false,
})
export class AssessmentScheduleMeetingComponent extends AbstractForm<
  AssessmentScheduleMeetingRequestModel,
  AssessmentScheduleMeetingSubmitModel,
  Record<string, IReferenceData[]>
> {
  @Input() evaluationDetails: EvaluationList;
  @Input() studentDetails: DashboardResponse;
  @Input() evaluationSk: string;
  @Input() actionText: string;
  @Input() referralSk: string;
  @Input() meetingSk: string;
  @Input() meetingType: MeetingType;
  @Input() isReSchedule: boolean = false;
  @Input() assessments: EvaluationList[] = [];
  @Input() nycIds: string;
  @Input() assessmentSk: string;
  @Output() close: EventEmitter<void> = new EventEmitter<void>();
  meetingSettingTypeENUM: typeof MeetingSettingType = MeetingSettingType;
  formModel: ISedmsFormModel = AssessmentScheduleMeetingFormModel;
  form: FormGroup;
  dataList: any[];
  studentData: any;
  studentProfile: any;
  loadSpinner: boolean;
  showRescheduleMeeting: boolean;
  public requiredAttendees = [];
  public optionalAttendeesItems = [];
  confirmationDialog: boolean = false;
  timePickerMin: Date = SCHEDULE_MEETING_CONSTANTS.timePickerDefaultMin;
  timePickerMax: Date = SCHEDULE_MEETING_CONSTANTS.timePickerDefaultMax;
  timePickerValue: Date = SCHEDULE_MEETING_CONSTANTS.timePickerDefaultValue;
  scheduleMeetingList: IMeetingDetails[] = [];
  meetingTypeEnum: typeof MeetingType = MeetingType;
  public format: FormatSettings = {
    displayFormat: 'MM/dd/yyyy',
    inputFormat: 'MM/dd/yyyy',
  };
  currentDate: Date = new Date(
    new Date().setHours(HOURS, MINUTES, SECONDS, MS)
  );
  userDetails: User;
  maxMeetingDate: Date;
  minMeetingDate: Date | null = null;
  pwnNoticeDate: Date;
  meetingStartTime: string;
  meetingEndTime: string;
  constructor(
    library: FaIconLibrary,
    route: ActivatedRoute,
    cdRef: ChangeDetectorRef,
    formBuilder: FormBuilder,
    dialogService: DialogService,
    public activatedRoute: ActivatedRoute,
    notificationService: NotificationService,
    private referenceStore: ReferenceStore,
    private dashboardService: DashboardService,
    private authStore: AuthStore,
    private evalTabComponent: EvaluationsTabComponent,
    private iepService: IEPService<SubmitScheduleMeetingModel>,
    private evaluationService: EvaluationService<any>
  ) {
    super(cdRef, route, formBuilder, dialogService, notificationService);
    library.addIcons(
      faCalendar,
      faStairs,
      faArrowRight,
      faClock,
      faIndent,
      faUsers,
      faLocationDot,
      faUser,
      faChalkboardUser
    );
    this.loadSpinner = true;
    this.authStore.UserInfo$.pipe(takeUntil(this.componentDestroyed)).subscribe(
      (userInfo: User) => {
        this.userDetails = userInfo;
      }
    );
    this.maxMeetingDate = moment().add(1, 'year').endOf('day').toDate(); //Maximum Date must be <= 1 year after current date
  }

  initEntity(
    entityData: any,
    referenceData: AssessmentScheduleMeetingRefData
  ): AssessmentScheduleMeetingRequestModel {
    return new AssessmentScheduleMeetingRequestModel(this.evaluationDetails);
  }

  get entity$(): Observable<any> {
    return of({});
  }

  get referenceData$(): Observable<IReferenceData[][]> {
    return combineLatest([
      this.referenceStore.extReq$,
      this.referenceStore.assessmentRejection$,
    ]);
  }

  initReferenceData([reason, assessmentRejection]: [
    IReferenceData[],
    IReferenceData[]
  ]): any { }

  initWatches(): void { }

  public override otherInitalizations(): void {
    this.getDropdownDetails();
    this.patchMeetingDetails();
    this.form.patchValue({
      evaluatorName: this.evaluationDetails.evaluatorName,
    });
    this.form.get('evaluatorName').disable();
    this.setMinMeetingDate();
  }

  setMinMeetingDate(): void {
    this.evaluationService
      .getNoticeDate(this.referralSk)
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((response: { noticeDate: Date }) => {
        this.minMeetingDate = response.noticeDate
          ? moment(response.noticeDate).startOf('day').toDate()
          : null; //Minimum Date must be >= Notice Date of PWN (Referrals)
        this.pwnNoticeDate = response.noticeDate;
      });
  }

  getEvalsData(): void {
    this.dashboardService
      .getEvalsData(this.studentDetails.referralSK)
      .subscribe((res: DashboardEvalResponse) => {
        this.assessments = this.isReSchedule
          ? res.evaluationList.filter(
            (a: EvaluationList) =>
              a.evaluationStatus === SCHEDULED && a.evaluatorName !== ' '
          )
          : res.evaluationList.filter(
            (a: EvaluationList) =>
              a.evaluationStatus === PENDING_SCHEDULING &&
              a.evaluatorName !== ' '
          );
      });
  }
  getTreeStructureData(filteredAttendees: any[]) {
    const attendees = filteredAttendees.reduce(function (r, a, i) {
      r[a.title] = r[a.title] || [];
      a.text =
        a.firstName + ' ' + a.lastName + (a.email ? ' , ' + a.email : '');
      a.id = i + 1;
      r[a.title].push(a);
      return r;
    }, Object.create(null));

    const dataAttendees = [];
    let idcount = 1000;
    for (const title in attendees) {
      dataAttendees.push({
        text: title,
        id: idcount,
        children: attendees[title],
      });
      idcount++;
    }
    return dataAttendees;
  }

  getDropdownDetails(): void {
    this.studentDetails.age = this.calculateAge(this.studentDetails.dob);
    this.loadSpinner = false;
  }

  calculateAge(dob: Date | string): number {
    const birthDate: Date = new Date(dob);
    const today: Date = new Date();
    let age: number = today.getFullYear() - birthDate.getFullYear();
    const monthDifference: number = today.getMonth() - birthDate.getMonth();
    if (
      monthDifference < ZERO_MONTH_DIFFERENCE ||
      (monthDifference === ZERO_MONTH_DIFFERENCE &&
        today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  }

  postCancelConfirmation(): void { }

  postSubmitConfirmation(): void {
    this.evalTabComponent.ngOnInit();
    this.close.next();
  }

  getAssessmets() {
    const assessments = [];
    this.form?.value?.assessmentsList?.forEach((assessment) => {
      assessments.push({ assessmentSk: assessment });
    });
    return assessments;
  }

  patchMeetingDetails(): void {
    if (this.meetingSk !== null) {
      this.iepService
        .getMeetingDetails(this.meetingSk, iepSections.meeting)
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe((data: IMeetingDetails) => {
          this.scheduleMeetingList = [data];

          const fromJsonData = data?.formJson && JSON.parse(data?.formJson)
          if (fromJsonData) {
            this.meetingStartTime = fromJsonData.meetingStartTime;
            this.meetingEndTime = fromJsonData.meetingEndTime;

            const formModel: ScheduleMeetingRequestModel =
              new ScheduleMeetingRequestModel(fromJsonData);
            this.form.patchValue(formModel);
          }
        });
    }
  }

  saveDetails(): any {
    const assessments: Assessment[] = [];
    assessments.push({ assessmentSk: this.evaluationDetails?.evaluationSk });
    const scheduleMeetingData: SubmitScheduleMeetingModel =
      new SubmitScheduleMeetingModel({
        ...this.form.value,
        noticeDate: this.pwnNoticeDate,
      });
    scheduleMeetingData.studentSk = this.studentDetails.studentSK;
    scheduleMeetingData.parentSk = this.evaluationDetails?.evaluationSk;
    scheduleMeetingData.assessments = assessments;
    scheduleMeetingData.nycId = this.nycIds;
    scheduleMeetingData.meetingType = 5;
    scheduleMeetingData.personSk = this.userDetails.personSk; // this.studentDetails.personSK;
    scheduleMeetingData.evaluationSk = this.evaluationDetails?.evaluationSk;
    scheduleMeetingData.pwnNoticeDate = this.pwnNoticeDate;
    if (
      this.actionText == 'Schedule Evaluation Meeting' ||
      this.actionText == 'Reschedule Evaluation Meeting'
    ) {
      scheduleMeetingData.subMeetingType =
        this.meetingSettingTypeENUM.Assessment;
    }
    if (this.meetingSk) {
      scheduleMeetingData.meetingSk = this.meetingSk;
      return this.iepService.updateMeeting(
        scheduleMeetingData,
        iepSections.meeting
      );
    } else {
      return this.iepService.submitMeeting(
        scheduleMeetingData,
        iepSections.meeting
      );
    }
  }

  get canSaveDraft(): boolean {
    return !this.form.valid;
  }

  get canSubmit(): boolean {
    return false;
  }

  openConfirmationDialog(): void {
    if (!this.viewMode) this.confirmationDialog = true;
    else this.onCancelSEM();
  }

  closeConfirmationDialog(): void {
    this.confirmationDialog = false;
  }

  onCancelSEM(): void {
    this.confirmationDialog = false;
    this.close.emit();
  }

  public tagMapper(tags: any[]): any[] {
    return tags.length < TAG_MAPPER_LENGTH ? tags : [tags];
  }

  getMeetingEndTimeValue(): Date | null {
    const meetinStartTime: string = this.form.get('meetingStartTime')?.value;
    if (meetinStartTime) {
      const startTime: Date = new Date(meetinStartTime);
      startTime.setMinutes(startTime.getMinutes() + ADDITIONAL_MINUTE);
      return startTime;
    }
    return null;
  }

  get formatMeetingTime(): string {
  const start = this.meetingStartTime ? moment(this.meetingStartTime, 'MM/DD/YYYY hh:mm A').format('MM/DD/YYYY, hh:mm A') : '';
  const end = this.meetingEndTime ? moment(this.meetingEndTime).format('hh:mm A') : '';

  if(start || end ) return  `${start} to ${end}`;
  return '';
}


}
